import {
  platformDelete,
  platformGet,
  platformGetPaginated,
  platformPost,
  platformPut,
} from '@/lib/platformApi';
import type { PlatformAdmin } from '@/stores/platformAuthStore';
import type {
  DeletionPreview,
  ManualOnboardResult,
  PlatformDashboard,
  PlatformImpersonation,
  PlatformPlan,
  PlatformSubscription,
  PlatformTenant,
  PlatformTenantDetail,
  PlatformSettings,
  PlatformSupportMeta,
  PlatformSupportStats,
  PlatformSupportThread,
  PlatformSupportTicket,
  PlatformWebhookEvent,
  RazorpayPing,
  StatusOption,
} from './types';

/* ---- Session ------------------------------------------------------------ */

export function platformLogin(payload: { email: string; password: string }) {
  return platformPost<{ token: string; admin: PlatformAdmin }>('/auth/login', payload);
}

export function fetchPlatformMe(): Promise<PlatformAdmin> {
  return platformGet<PlatformAdmin>('/me');
}

export function platformLogout(): Promise<unknown> {
  return platformPost('/auth/logout');
}

/* ---- Dashboard ---------------------------------------------------------- */

export function fetchPlatformDashboard(): Promise<PlatformDashboard> {
  return platformGet<PlatformDashboard>('/dashboard');
}

/* ---- Workspaces --------------------------------------------------------- */

export function fetchTenants(params: Record<string, unknown>) {
  return platformGetPaginated<PlatformTenant>('/tenants', params);
}

/**
 * Manual onboarding.
 *
 * Runs the same transaction as public signup, so the workspace comes back
 * fully seeded — roles, first outlet, tax rates, payment methods.
 */
export function createTenant(payload: Record<string, unknown>) {
  return platformPost<ManualOnboardResult>('/tenants', payload);
}

export function fetchTenant(uuid: string): Promise<PlatformTenantDetail> {
  return platformGet<PlatformTenantDetail>(`/tenants/${uuid}`);
}

export function updateTenant(uuid: string, payload: Record<string, unknown>) {
  return platformPut<PlatformTenant>(`/tenants/${uuid}`, payload);
}

export function suspendTenant(uuid: string, reason: string) {
  return platformPost<PlatformTenant & { tokens_revoked: number }>(`/tenants/${uuid}/suspend`, {
    reason,
  });
}

export function restoreTenant(uuid: string) {
  return platformPost<PlatformTenant>(`/tenants/${uuid}/restore`);
}

export function impersonateTenant(uuid: string, allowWrites: boolean) {
  return platformPost<{ url: string }>(`/tenants/${uuid}/impersonate`, {
    allow_writes: allowWrites,
  });
}

/** Read-only. Shown before the destructive step is ever offered. */
export function fetchDeletionPreview(uuid: string): Promise<DeletionPreview> {
  return platformGet<DeletionPreview>(`/tenants/${uuid}/deletion-preview`);
}

/**
 * Permanent. Everything in the workspace goes.
 *
 * `confirm` must be the slug and is re-checked server-side — the dialog's own
 * check only stops a slipped mouse.
 */
export function deleteTenant(uuid: string, payload: { confirm: string; reason?: string }) {
  return platformDelete<{ deleted: boolean; name: string; rows_deleted: number; files_deleted: number }>(
    `/tenants/${uuid}`,
    payload,
  );
}

export function fetchTenantStatuses(): Promise<StatusOption[]> {
  return platformGet<StatusOption[]>('/tenant-statuses');
}

/* ---- Pricing ------------------------------------------------------------ */

export function fetchPlans(): Promise<PlatformPlan[]> {
  return platformGet<PlatformPlan[]>('/plans');
}

export function createPlan(payload: Record<string, unknown>) {
  return platformPost<PlatformPlan>('/plans', payload);
}

/** Plans bind by SLUG, not id (Plan::getRouteKeyName). */
export function updatePlan(slug: string, payload: Record<string, unknown>) {
  return platformPut<PlatformPlan>(`/plans/${slug}`, payload);
}

export function deletePlan(slug: string) {
  return platformDelete(`/plans/${slug}`);
}

/* ---- Subscriptions ------------------------------------------------------ */

export function fetchSubscriptions(params: Record<string, unknown>) {
  return platformGetPaginated<PlatformSubscription>('/subscriptions', params);
}

export function updateSubscription(id: number, payload: Record<string, unknown>) {
  return platformPut<PlatformSubscription>(`/subscriptions/${id}`, payload);
}

export function fetchSubscriptionStatuses(): Promise<StatusOption[]> {
  return platformGet<StatusOption[]>('/subscription-statuses');
}

/* ---- Webhooks ----------------------------------------------------------- */

export function fetchWebhookEvents(params: Record<string, unknown>) {
  return platformGetPaginated<PlatformWebhookEvent>('/webhook-events', params);
}

export function fetchWebhookEvent(id: number): Promise<PlatformWebhookEvent> {
  return platformGet<PlatformWebhookEvent>(`/webhook-events/${id}`);
}

export function replayWebhookEvent(id: number) {
  return platformPost<PlatformWebhookEvent>(`/webhook-events/${id}/replay`);
}

export function fetchWebhookEventTypes(): Promise<string[]> {
  return platformGet<string[]>('/webhook-event-types');
}

/* ---- Us ----------------------------------------------------------------- */

export function fetchPlatformAdmins(): Promise<PlatformAdmin[]> {
  return platformGet<PlatformAdmin[]>('/admins');
}

export function createPlatformAdmin(payload: Record<string, unknown>) {
  return platformPost<PlatformAdmin>('/admins', payload);
}

export function updatePlatformAdmin(id: number, payload: Record<string, unknown>) {
  return platformPut<PlatformAdmin>(`/admins/${id}`, payload);
}

export function deletePlatformAdmin(id: number) {
  return platformDelete(`/admins/${id}`);
}

export function fetchImpersonations(): Promise<PlatformImpersonation[]> {
  return platformGet<PlatformImpersonation[]>('/impersonations');
}

/* ---- Operator settings --------------------------------------------------- */

export function fetchPlatformSettings(): Promise<PlatformSettings> {
  return platformGet<PlatformSettings>('/settings');
}

/**
 * Save.
 *
 * A blank secret means "leave it alone" on the server, which is what lets the
 * form omit a value it was never given. Clearing is the explicit `clear` array.
 */
export function updatePlatformSettings(payload: Record<string, unknown>) {
  return platformPut<PlatformSettings>('/settings', payload);
}

export function testRazorpay(): Promise<RazorpayPing> {
  return platformPost<RazorpayPing>('/settings/razorpay/test');
}

/**
 * Send one real email with the settings as they stand.
 *
 * The alternative to this button is finding out from a customer who could not
 * reset their password.
 */
export function testEmail(to: string): Promise<RazorpayPing> {
  return platformPost<RazorpayPing>('/settings/email/test', { to });
}

/* ---- Support ------------------------------------------------------------- */

export function fetchSupportTickets(params: Record<string, unknown>) {
  return platformGetPaginated<PlatformSupportTicket>('/support/tickets', params);
}

/** Tickets bind by reference — VT-00042, the thing quoted in emails. */
export function fetchSupportThread(reference: string): Promise<PlatformSupportThread> {
  return platformGet<PlatformSupportThread>(`/support/tickets/${reference}`);
}

export function replyToSupportTicket(
  reference: string,
  payload: { body: string; is_internal?: boolean; resolve?: boolean },
) {
  return platformPost<PlatformSupportThread>(`/support/tickets/${reference}/messages`, payload);
}

export function updateSupportTicket(reference: string, payload: Record<string, unknown>) {
  return platformPut<PlatformSupportThread>(`/support/tickets/${reference}`, payload);
}

export function fetchSupportStats(): Promise<PlatformSupportStats> {
  return platformGet<PlatformSupportStats>('/support/stats');
}

export function fetchSupportMeta(): Promise<PlatformSupportMeta> {
  return platformGet<PlatformSupportMeta>('/support/meta');
}
